import React, { useEffect, useId, useRef, useState } from 'react';

import { SHEET_CLOSE_ARIA_LABEL } from '../../constants/sheetCopy';
import { useAuth } from '../../contexts/AuthContext';
import type { AuthPromptRequest, AuthPromptTab } from '../../contexts/AuthPromptContext';
import { useCart } from '../../contexts/CartContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TotpChallenge, TotpSessionExpiredError } from '../../services/cognitoAuthService';
import {
  FILLS,
  GRADIENTS,
  PHOTO_TEXT_SCRIM_FADE_PX,
  RADII,
  SIZING,
  TYPE,
} from '../../styles/tokens';
import { ProductType, User } from '../../types';
import { summariseCartForCheckout } from '../../utils/cartCheckoutSummary';
import { deriveCartItemDisplay } from '../../utils/cartItemDisplay';
import { pendingSignInActionFor } from '../../utils/pendingSignInAction';
import { isPlaceholderImageUrl } from '../../utils/placeholderImage';
import {
  Sheet,
  SheetFooter,
  SheetScrollArea,
  SheetTitle,
  useSheetKeyboardLifted,
} from '../common/Sheet';
import { ArrowBackIcon, CloseIcon } from '../icons';
import ForgotPasswordForm from './ForgotPasswordForm';
import GoogleSignInButton, { OrDivider } from './GoogleSignInButton';
import LogInCodeStep from './LogInCodeStep';
import LogInForm from './LogInForm';
import LogInTotpStep from './LogInTotpStep';
import SignUpForm, { SignUpStep, SignUpStepNavigation } from './SignUpForm';

export const AUTH_PROMPT_TITLE = 'Log in or sign up';
export const AUTH_PROMPT_LOG_IN_TAB_LABEL = 'Log in';
export const AUTH_PROMPT_SIGN_UP_TAB_LABEL = 'Sign up';
export const AUTH_PROMPT_GOOGLE_LABEL = 'Continue with Google';
export const AUTH_PROMPT_DIVIDER_LABEL = 'or use email';
export const AUTH_PROMPT_AUTO_LOG_IN_FAILED =
  "Your account is ready, but we couldn't log you in automatically. Please log in below.";
export const AUTH_PROMPT_VERIFIED_LOG_IN_FAILED =
  "Your email is verified, but we couldn't log you in automatically. Please log in below.";
export const AUTH_PROMPT_PASSWORD_RESET_NOTICE =
  'Your password has been reset. Log in with your new password.';
export const AUTH_PROMPT_SESSION_EXPIRED_NOTICE = 'Your session has expired. Log in again.';
export const AUTH_PROMPT_WISHLIST_TITLE = 'Save it for later';
export const AUTH_PROMPT_WISHLIST_EYEBROW = 'Saving to your wishlist';
export const AUTH_PROMPT_WISHLIST_FALLBACK_NAME = 'This listing';
export const AUTH_PROMPT_WISHLIST_SUB_LINE =
  'Your wishlist follows you to every device once you have an account.';
export const AUTH_PROMPT_CHECKOUT_TITLE = 'Log in to check out';
export const AUTH_PROMPT_CHECKOUT_EYEBROW = 'Checking out';
export const AUTH_PROMPT_CHECKOUT_SUB_LINE =
  "Nothing in your cart changes — you'll pick up right where you left off.";
const SIGN_UP_NEXT_LABEL = 'Next';
const SIGN_UP_CREATE_LABEL = 'Create Account';
const SIGN_UP_BACK_LABEL = 'Back';

const DESKTOP_WIDTH = '440px';
const DESKTOP_CONTEXT_WIDTH = '720px';
const DESKTOP_CHECKOUT_WIDTH = '880px';

/** Dialog title and desktop width for each reason the pop-up opened. */
const INTENT_PRESENTATION: Record<
  AuthPromptRequest['intent'],
  { title: string; desktopWidth: `${number}px` }
> = {
  signIn: { title: AUTH_PROMPT_TITLE, desktopWidth: DESKTOP_WIDTH },
  wishlist: { title: AUTH_PROMPT_WISHLIST_TITLE, desktopWidth: DESKTOP_CONTEXT_WIDTH },
  checkout: { title: AUTH_PROMPT_CHECKOUT_TITLE, desktopWidth: DESKTOP_CHECKOUT_WIDTH },
};
const CONTEXT_PANEL_COLUMN = '260px';
const PHONE_STRIP_HEIGHT = '96px';
const DESKTOP_PADDING = '32px';
const PHONE_PADDING = '24px';
const PANEL_HORIZONTAL_PADDING_PHONE_PX = 16;
const PANEL_HORIZONTAL_PADDING_DESKTOP_PX = 24;
const PANEL_VERTICAL_PADDING_PHONE_PX = 12;
const PANEL_VERTICAL_PADDING_DESKTOP_PX = 24;
const SCRIM_BAND_TEST_ID = 'auth-prompt-text-scrim';
const PHONE_MAX_HEIGHT = '92dvh';
const FOCUS_RING_ROOM = '4px';
/** Aligns the pinned footer with the form column beside the photo panel. */
const DESKTOP_CONTEXT_FOOTER_STYLE: React.CSSProperties = {
  padding: `0 ${DESKTOP_PADDING} ${DESKTOP_PADDING} calc(${CONTEXT_PANEL_COLUMN} + ${DESKTOP_PADDING})`,
};
const LOG_IN_PREFIX = 'prompt';

const TAB_ORDER: AuthPromptTab[] = ['logIn', 'signUp'];
const TAB_LABELS: Record<AuthPromptTab, string> = {
  logIn: AUTH_PROMPT_LOG_IN_TAB_LABEL,
  signUp: AUTH_PROMPT_SIGN_UP_TAB_LABEL,
};

export interface AuthPromptDialogProps {
  request: AuthPromptRequest;
  initialTab: AuthPromptTab;
  onTabChange: (tab: AuthPromptTab) => void;
  /** The guest is signed in; the host closes the pop-up and finishes any pending action. */
  onSignedIn: (user: User | null) => void;
  /** The guest dismissed the pop-up; nothing is finished. */
  onClose: () => void;
}

interface ContextPanelProps {
  eyebrow: string;
  headline: string;
  headlineStyle: React.CSSProperties;
  subLine: string;
  imageUrl?: string;
  /** The warm wash shown behind, or instead of, the photo. */
  wash: string;
  isMobile: boolean;
  style: React.CSSProperties;
}

const ContextPanel: React.FC<ContextPanelProps> = ({
  eyebrow,
  headline,
  headlineStyle,
  subLine,
  imageUrl,
  wash,
  isMobile,
  style,
}) => {
  const keyboardLifted = useSheetKeyboardLifted();
  const text = (
    <>
      <span
        className="form-label"
        style={{ marginBottom: '4px', ...(imageUrl ? { color: FILLS.textLight } : {}) }}
      >
        {eyebrow}
      </span>
      <p style={{ ...headlineStyle, margin: 0, color: FILLS.textPrimary }}>{headline}</p>
      {!isMobile && (
        <p
          style={{
            ...TYPE.secondary,
            lineHeight: 1.5,
            margin: '12px 0 0',
            color: FILLS.textLight,
          }}
        >
          {subLine}
        </p>
      )}
    </>
  );
  const horizontalPadding = isMobile
    ? PANEL_HORIZONTAL_PADDING_PHONE_PX
    : PANEL_HORIZONTAL_PADDING_DESKTOP_PX;
  const verticalPadding = isMobile
    ? PANEL_VERTICAL_PADDING_PHONE_PX
    : PANEL_VERTICAL_PADDING_DESKTOP_PX;
  const textPadding = `${verticalPadding}px ${horizontalPadding}px`;
  if (isMobile && keyboardLifted) return null;
  return (
    <div
      data-testid="auth-prompt-context-panel"
      style={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        backgroundImage: wash,
        padding: imageUrl ? 0 : textPadding,
        ...style,
      }}
    >
      {imageUrl && (
        <>
          <div
            className="card-media"
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, isolation: 'isolate' }}
          >
            <img
              className="card-image"
              src={imageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
          <div
            data-testid={SCRIM_BAND_TEST_ID}
            style={{
              position: 'relative',
              backgroundImage: GRADIENTS.photoTextScrim,
              padding: `${PHOTO_TEXT_SCRIM_FADE_PX + verticalPadding}px ${horizontalPadding}px ${verticalPadding}px`,
            }}
          >
            {text}
          </div>
        </>
      )}
      {!imageUrl && <div style={{ position: 'relative' }}>{text}</div>}
    </div>
  );
};

type PanelLayoutProps = Pick<ContextPanelProps, 'isMobile' | 'style'>;

const WishlistContextPanel: React.FC<
  PanelLayoutProps & { item: Extract<AuthPromptRequest, { intent: 'wishlist' }>['item'] }
> = ({ item, isMobile, style }) => (
  <ContextPanel
    eyebrow={AUTH_PROMPT_WISHLIST_EYEBROW}
    headline={item.name || AUTH_PROMPT_WISHLIST_FALLBACK_NAME}
    headlineStyle={isMobile ? TYPE.itemName : TYPE.sheetTitle}
    subLine={AUTH_PROMPT_WISHLIST_SUB_LINE}
    imageUrl={item.imageUrl}
    wash={
      item.kind === 'service' ? GRADIENTS.listingHeroServices : GRADIENTS.listingHeroAccommodations
    }
    isMobile={isMobile}
    style={style}
  />
);

const CheckoutContextPanel: React.FC<PanelLayoutProps> = ({ isMobile, style }) => {
  const { cartItems, getItemCount, getTotalPrice, getChargeTimingGroups } = useCart();
  const { label } = summariseCartForCheckout(
    getItemCount(),
    getTotalPrice(),
    getChargeTimingGroups().awaitingQuote.length,
  );
  const firstLineImage = cartItems[0] ? deriveCartItemDisplay(cartItems[0]).image : '';
  const firstLineWash =
    cartItems[0]?.type === ProductType.SERVICE
      ? GRADIENTS.listingHeroServices
      : GRADIENTS.listingHeroAccommodations;
  return (
    <ContextPanel
      eyebrow={AUTH_PROMPT_CHECKOUT_EYEBROW}
      headline={label}
      headlineStyle={TYPE.priceTotal}
      subLine={AUTH_PROMPT_CHECKOUT_SUB_LINE}
      imageUrl={isPlaceholderImageUrl(firstLineImage) ? undefined : firstLineImage}
      wash={firstLineWash}
      isMobile={isMobile}
      style={style}
    />
  );
};

/** Reports the sign-up form's current step upward; renders nothing. */
const SignUpStepReporter: React.FC<{
  step: SignUpStep;
  onStep: (step: SignUpStep) => void;
}> = ({ step, onStep }) => {
  useEffect(() => {
    onStep(step);
  }, [step, onStep]);
  return null;
};

interface DialogHeaderProps {
  title: string;
  onClose: () => void;
  style?: React.CSSProperties;
}

/** Title row; shrinks to a slim bar while the on-screen keyboard is up. */
const DialogHeader: React.FC<DialogHeaderProps> = ({ title, onClose, style }) => {
  const compact = useSheetKeyboardLifted();
  return (
    <div
      data-testid="auth-prompt-header"
      data-compact={String(compact)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: compact ? '4px' : '16px',
        ...style,
      }}
    >
      <SheetTitle
        style={{
          ...(compact ? TYPE.itemName : TYPE.sheetTitle),
          color: FILLS.textPrimary,
          margin: 0,
        }}
      >
        {title}
      </SheetTitle>
      <button
        type="button"
        aria-label={SHEET_CLOSE_ARIA_LABEL}
        onClick={onClose}
        style={{
          minWidth: SIZING.touchTarget,
          minHeight: SIZING.touchTarget,
          margin: compact ? '-12px -12px -12px 0' : '-8px -12px -8px 0',
          background: 'none',
          border: 'none',
          color: FILLS.textPrimary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

const AuthPromptDialog: React.FC<AuthPromptDialogProps> = ({
  request,
  initialTab,
  onTabChange,
  onSignedIn,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const { login, completeTotpSignIn } = useAuth();
  const idBase = useId();

  // The log-in field autofocuses on mount, before `Sheet` records the opener, so the
  // trigger is captured here (first render) and handed to `Sheet` as the focus fallback.
  const [trigger] = useState<Element | null>(() => document.activeElement);
  const returnFocusToTrigger = () => (trigger instanceof HTMLElement ? trigger : null);

  const [tab, setTab] = useState<AuthPromptTab>(initialTab);
  const [signUpStep, setSignUpStep] = useState<SignUpStep>('basic');
  // The Log in form's e-mail (and the error to show above it) after the pop-up hands the
  // guest back to it: an automatic log-in that failed, or "Use a different e-mail".
  const [logInPrefill, setLogInPrefill] = useState<{
    email: string;
    message?: string;
    notice?: string;
  } | null>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(
    request.intent === 'signIn' && request.entry === 'forgotPassword',
  );
  const [codeStepEmail, setCodeStepEmail] = useState<string | null>(null);
  // The typed password lives in this ref only: never state, storage or a prop of the code step.
  const codeStepPassword = useRef('');
  const [totpStepEmail, setTotpStepEmail] = useState<string | null>(null);
  // The authenticator challenge lives in this ref only: never state, storage or a prop of the step.
  const totpChallenge = useRef<TotpChallenge | null>(null);

  const tabId = (value: AuthPromptTab) => `${idBase}-tab-${value}`;
  const panelId = `${idBase}-panel`;

  const switchTab = (next: AuthPromptTab) => {
    setTab(next);
    setSignUpStep('basic');
    onTabChange(next);
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TAB_ORDER.indexOf(tab);
    let nextIndex: number;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TAB_ORDER.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TAB_ORDER.length - 1;
    else return;
    event.preventDefault();
    const next = TAB_ORDER[nextIndex];
    switchTab(next);
    document.getElementById(tabId(next))?.focus();
  };

  const handleForgotPassword = () => {
    setLogInPrefill((prefill) => (prefill ? { email: prefill.email } : prefill));
    setForgotPasswordOpen(true);
  };

  const handleResetFinished = (email: string) => {
    setLogInPrefill({ email, notice: AUTH_PROMPT_PASSWORD_RESET_NOTICE });
    setForgotPasswordOpen(false);
  };

  const handleBackToLogIn = () => setForgotPasswordOpen(false);

  const signInAfterVerification = async (
    { email, password }: { email: string; password: string },
    failureMessage: string,
  ) => {
    codeStepPassword.current = '';
    try {
      const signedInUser = await login(email, password);
      onSignedIn(signedInUser);
    } catch {
      setCodeStepEmail(null);
      setLogInPrefill({ email, message: failureMessage });
      switchTab('logIn');
    }
  };

  const handleSignUpVerified = (credentials: { email: string; password: string }) =>
    signInAfterVerification(credentials, AUTH_PROMPT_AUTO_LOG_IN_FAILED);

  const handleLogInEmailUnverified = ({ email, password }: { email: string; password: string }) => {
    codeStepPassword.current = password;
    setCodeStepEmail(email);
  };

  const handleLogInCodeVerified = () => {
    if (codeStepEmail === null) return;
    return signInAfterVerification(
      { email: codeStepEmail, password: codeStepPassword.current },
      AUTH_PROMPT_VERIFIED_LOG_IN_FAILED,
    );
  };

  const handleUseDifferentEmail = () => {
    codeStepPassword.current = '';
    setLogInPrefill({ email: codeStepEmail ?? '' });
    setCodeStepEmail(null);
  };

  const handleLogInTotpRequired = ({
    email,
    username,
    session,
  }: TotpChallenge & { email: string }) => {
    totpChallenge.current = { username, session };
    setTotpStepEmail(email);
  };

  const handleVerifyTotpCode = async (code: string) => {
    const challenge = totpChallenge.current;
    if (challenge === null) return;
    try {
      const signedInUser = await completeTotpSignIn(challenge, code);
      totpChallenge.current = null;
      onSignedIn(signedInUser);
    } catch (caught: unknown) {
      if (!(caught instanceof TotpSessionExpiredError)) throw caught;
      totpChallenge.current = null;
      setLogInPrefill({ email: totpStepEmail ?? '', message: caught.message });
      setTotpStepEmail(null);
    }
  };

  const handleUseDifferentAccount = () => {
    totpChallenge.current = null;
    setLogInPrefill({ email: totpStepEmail ?? '' });
    setTotpStepEmail(null);
  };

  const googleMinHeight = isMobile ? SIZING.touchTarget : SIZING.controlHeight;
  const tabHeight = isMobile ? SIZING.touchTarget : SIZING.controlHeight;
  const isLogInCodeStep = tab === 'logIn' && codeStepEmail !== null;
  const isTotpStep = tab === 'logIn' && totpStepEmail !== null;
  const showTablist =
    !forgotPasswordOpen &&
    !isLogInCodeStep &&
    !isTotpStep &&
    !(tab === 'signUp' && signUpStep === 'verification');
  const showGoogle =
    !forgotPasswordOpen &&
    !isLogInCodeStep &&
    !isTotpStep &&
    (tab === 'logIn' || signUpStep === 'basic');

  const googleBlock = (
    <>
      <GoogleSignInButton
        label={AUTH_PROMPT_GOOGLE_LABEL}
        minHeight={googleMinHeight}
        actionAfterSignIn={pendingSignInActionFor(request) ?? undefined}
        captureReturnTo={!(request.intent === 'signIn' && request.entry)}
      />
      <OrDivider label={AUTH_PROMPT_DIVIDER_LABEL} />
    </>
  );

  const renderTab = (value: AuthPromptTab) => {
    const selected = tab === value;
    return (
      <button
        key={value}
        type="button"
        role="tab"
        id={tabId(value)}
        aria-selected={selected}
        aria-controls={panelId}
        tabIndex={selected ? 0 : -1}
        onClick={() => switchTab(value)}
        onKeyDown={handleTabKeyDown}
        style={{
          flex: 1,
          minHeight: tabHeight,
          borderRadius: RADII.pill,
          border: `1px solid ${selected ? FILLS.goldAlpha30 : 'transparent'}`,
          background: selected ? FILLS.goldAlpha15 : FILLS.chip,
          color: selected ? FILLS.gold : FILLS.textLight,
          ...TYPE.chip,
          cursor: 'pointer',
        }}
      >
        {TAB_LABELS[value]}
      </button>
    );
  };

  const renderSignUpNavigation = ({
    step,
    isLoading,
    tosAccepted,
    basicFormId,
    goBack,
    goNext,
  }: SignUpStepNavigation) => {
    if (step === 'verification') return null;
    const spinner = <span className="spinner" style={{ width: '24px', height: '24px' }} />;
    return (
      <SheetFooter>
        {step === 'basic' ? (
          <button className="btn-gold" type="submit" form={basicFormId} disabled={isLoading}>
            {isLoading ? spinner : SIGN_UP_NEXT_LABEL}
          </button>
        ) : (
          <>
            <button
              className="btn-gold"
              type="button"
              onClick={goNext}
              disabled={isLoading || !tosAccepted}
            >
              {isLoading ? spinner : SIGN_UP_CREATE_LABEL}
            </button>
            <button
              type="button"
              onClick={goBack}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                margin: '8px auto 0',
                minHeight: isMobile ? SIZING.touchTarget : undefined,
                padding: '8px 16px',
                color: FILLS.gold,
                background: 'none',
                border: 'none',
                cursor: isLoading ? 'default' : 'pointer',
                opacity: isLoading ? 0.4 : 1,
                ...TYPE.secondary,
                fontWeight: 500,
              }}
            >
              <ArrowBackIcon style={{ width: '20px', height: '20px' }} />
              {SIGN_UP_BACK_LABEL}
            </button>
          </>
        )}
      </SheetFooter>
    );
  };

  const hasContext = request.intent !== 'signIn';
  const { title, desktopWidth } = INTENT_PRESENTATION[request.intent];
  const isDesktopContext = hasContext && !isMobile;
  const padding = isDesktopContext ? '0px' : isMobile ? PHONE_PADDING : DESKTOP_PADDING;

  const panelStyle: React.CSSProperties = isMobile
    ? {
        flexShrink: 0,
        height: PHONE_STRIP_HEIGHT,
        marginBottom: '16px',
        borderRadius: RADII.card,
      }
    : { gridColumn: 1, gridRow: '1 / span 2' };

  const header = (
    <DialogHeader
      title={title}
      onClose={onClose}
      style={
        isDesktopContext
          ? {
              gridColumn: 2,
              gridRow: 1,
              padding: `${DESKTOP_PADDING} ${DESKTOP_PADDING} 0`,
            }
          : undefined
      }
    />
  );
  const body = (
    <SheetScrollArea
      style={
        isDesktopContext
          ? {
              gridColumn: 2,
              gridRow: 2,
              margin: `0 calc(${DESKTOP_PADDING} - ${FOCUS_RING_ROOM}) calc(${DESKTOP_PADDING} - ${FOCUS_RING_ROOM})`,
            }
          : undefined
      }
      scrollerStyle={{ padding: FOCUS_RING_ROOM, margin: `-${FOCUS_RING_ROOM}` }}
    >
      {request.intent === 'signIn' && request.reason === 'sessionExpired' && (
        <div className="alert alert-info" role="alert" style={{ marginBottom: '16px' }}>
          {AUTH_PROMPT_SESSION_EXPIRED_NOTICE}
        </div>
      )}

      {showGoogle && googleBlock}

      {showTablist && (
        <div
          role="tablist"
          aria-label={AUTH_PROMPT_TITLE}
          style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}
        >
          {TAB_ORDER.map(renderTab)}
        </div>
      )}

      <div role="tabpanel" id={panelId} aria-labelledby={tabId(tab)}>
        {forgotPasswordOpen ? (
          <ForgotPasswordForm
            onReset={handleResetFinished}
            onBack={handleBackToLogIn}
            pinSubmitToSheetFooter
          />
        ) : isLogInCodeStep ? (
          <LogInCodeStep
            email={codeStepEmail}
            onVerified={handleLogInCodeVerified}
            onUseDifferentEmail={handleUseDifferentEmail}
            pinPrimaryToSheetFooter
          />
        ) : isTotpStep ? (
          <LogInTotpStep
            onVerify={handleVerifyTotpCode}
            onUseDifferentAccount={handleUseDifferentAccount}
            pinPrimaryToSheetFooter
          />
        ) : tab === 'logIn' ? (
          <>
            {logInPrefill?.notice && (
              <div className="alert alert-success" role="status" style={{ marginBottom: '16px' }}>
                {logInPrefill.notice}
              </div>
            )}
            {logInPrefill?.message && (
              <div
                className="alert alert-error"
                role="alert"
                style={{ marginBottom: '16px', color: FILLS.errorText }}
              >
                {logInPrefill.message}
              </div>
            )}
            <LogInForm
              onSignedIn={onSignedIn}
              submitLabel={AUTH_PROMPT_LOG_IN_TAB_LABEL}
              idPrefix={LOG_IN_PREFIX}
              onForgotPassword={handleForgotPassword}
              forgotPasswordMinHeight={isMobile ? SIZING.touchTarget : undefined}
              prefilledEmail={logInPrefill?.email}
              onEmailUnverified={handleLogInEmailUnverified}
              onTotpRequired={handleLogInTotpRequired}
              pinSubmitToSheetFooter
            />
          </>
        ) : (
          <SignUpForm
            compactStepHeading
            pinCodeStepPrimaryToSheetFooter
            onVerified={handleSignUpVerified}
            renderBeforeStepper={(step) => (
              <SignUpStepReporter step={step} onStep={setSignUpStep} />
            )}
            renderStepNavigation={renderSignUpNavigation}
          />
        )}
      </div>
    </SheetScrollArea>
  );
  return (
    <Sheet
      layout={isMobile ? 'bottom' : 'centered'}
      width={desktopWidth}
      maxHeight={PHONE_MAX_HEIGHT}
      padding={padding}
      footerSlot
      footerStyle={isDesktopContext ? DESKTOP_CONTEXT_FOOTER_STYLE : undefined}
      onClose={onClose}
      returnFocusFallback={returnFocusToTrigger}
    >
      {hasContext ? (
        <div
          style={
            isMobile
              ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
              : {
                  display: 'grid',
                  gridTemplateColumns: `${CONTEXT_PANEL_COLUMN} minmax(0, 1fr)`,
                  gridTemplateRows: 'auto minmax(0, 1fr)',
                  flex: 1,
                  minHeight: 0,
                }
          }
        >
          {header}
          {request.intent === 'wishlist' && (
            <WishlistContextPanel item={request.item} isMobile={isMobile} style={panelStyle} />
          )}
          {request.intent === 'checkout' && (
            <CheckoutContextPanel isMobile={isMobile} style={panelStyle} />
          )}
          {body}
        </div>
      ) : (
        <>
          {header}
          {body}
        </>
      )}
    </Sheet>
  );
};

export default AuthPromptDialog;
